import React, { useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, Save, Trash2, Share2, Copy, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, Input, Select, EmptyState, DeadlineBadge, Btn, Field, ModalShell } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";
import StaffBookingDetail from "@/pages/staff/StaffBookingDetail";
import { NewRequestModal } from "@/components/NewRequestModal";
import { loadStaffBookings, loadRefData, loadAccessibleVehicles } from "@/lib/staffData";
import { updateBucket, deadlineInfo, matchesDeadlineFilter, fmtDate, fmtDateTime, INTERNAL_STATUSES, REQUEST_LABEL, formatReg, normalizeReg, ROLE_LABEL } from "@/lib/vda";

const _ts = (x) => (x ? new Date(x).getTime() : null);
function _cmpNull(a, b, asc) {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // nulls always last
  if (b == null) return -1;
  return asc ? a - b : b - a;
}
const SORT_OPTIONS = [
  { key: "overdue_first", label: "Most overdue first" },
  { key: "deadline_soonest", label: "Deadline soonest" },
  { key: "deadline_latest", label: "Deadline latest" },
  { key: "appointment_soonest", label: "Appointment soonest" },
  { key: "customer", label: "Customer" },
  { key: "registration", label: "Registration" },
  { key: "recently_updated", label: "Recently updated" },
  { key: "oldest_update", label: "Oldest update" },
  { key: "newest_added", label: "Newest added" },
  { key: "oldest_added", label: "Oldest added" },
];
const SORTERS = {
  overdue_first: (a, b) => _cmpNull(_ts(a.next_update_due_at), _ts(b.next_update_due_at), true),
  deadline_soonest: (a, b) => _cmpNull(_ts(a.next_update_due_at), _ts(b.next_update_due_at), true),
  deadline_latest: (a, b) => _cmpNull(_ts(a.next_update_due_at), _ts(b.next_update_due_at), false),
  appointment_soonest: (a, b) => _cmpNull(_ts(a.appointment_at), _ts(b.appointment_at), true),
  customer: (a, b) => (a.customers?.name || "").localeCompare(b.customers?.name || ""),
  registration: (a, b) => (a.vehicles?.registration || "").localeCompare(b.vehicles?.registration || ""),
  recently_updated: (a, b) => _cmpNull(_ts(a.updated_at), _ts(b.updated_at), false),
  oldest_update: (a, b) => _cmpNull(_ts(a.updated_at), _ts(b.updated_at), true),
  newest_added: (a, b) => _cmpNull(_ts(a.created_at), _ts(b.created_at), false),
  oldest_added: (a, b) => _cmpNull(_ts(a.created_at), _ts(b.created_at), true),
};

function isToday(d) {
  if (!d) return false;
  const x = new Date(d), n = new Date();
  return x.toDateString() === n.toDateString();
}

const DEADLINE_FILTERS = [
  { key: "", label: "All open" },
  { key: "today", label: "Due today" },
  { key: "due_soon", label: "Due soon" },
  { key: "overdue", label: "Overdue" },
  { key: "none", label: "No deadline" },
];

const QUEUES = [
  { key: "all", label: "All open", tone: "green", test: (b) => b.internal_status !== "completed" },
  { key: "new", label: "New requests", tone: "clay", test: (b) => b.internal_status === "new_request" },
  { key: "place", label: "Bookings to place", tone: "muted", test: (b) => ["new_request", "assigned_to_pod"].includes(b.internal_status) },
  { key: "confirm", label: "Confirmation required", tone: "muted", test: (b) => ["garage_contacted", "awaiting_garage_response"].includes(b.internal_status) },
  { key: "appt", label: "Appointments today", tone: "green", test: (b) => isToday(b.appointment_at) && b.internal_status !== "completed" },
  { key: "soon", label: "Updates due soon", tone: "amber", test: (b) => b.internal_status !== "completed" && updateBucket(b.next_update_due_at).key === "due_soon" },
  { key: "now", label: "Updates required", tone: "amber", test: (b) => b.internal_status !== "completed" && updateBucket(b.next_update_due_at).key === "due_now" },
  { key: "overdue", label: "Overdue", tone: "red", test: (b) => b.internal_status !== "completed" && updateBucket(b.next_update_due_at).key === "overdue" },
  { key: "authority", label: "Awaiting authority", tone: "muted", test: (b) => b.internal_status === "awaiting_internal_authority" },
  { key: "ready", label: "Ready for collection", tone: "green", test: (b) => b.internal_status === "vehicle_complete" },
  { key: "escalated", label: "Escalated · repeat chases", tone: "red", test: (b) => (b.chase_count || 0) >= 2 && b.internal_status !== "completed" },
  { key: "chased", label: "Drivers awaiting updates", tone: "amber", test: (b) => (b.chase_count || 0) > 0 && b.internal_status !== "completed" },
];

// A request auto-escalates once a driver has chased it repeatedly (see backend
// CHASE_ESCALATE_AT). Escalated open requests jump to the top of every queue.
const isEscalated = (b) => (b.chase_count || 0) >= 2 && b.internal_status !== "completed";

export default function AEDashboard() {
  const nav = useNavigate();
  const { session, profile } = useAuth();
  const uid = session?.user?.id;
  const { data, loading, reload: reloadBookings } = useAsync(async () => ({ bookings: await loadStaffBookings(), ref: await loadRefData(), cover: await api.coverMine().catch(() => ({ booking_request_ids: [] })) }), []);
  const [queue, setQueue] = useState("all");
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState("");
  const [status, setStatus] = useState("");
  const [mine, setMine] = useState(false);
  const [dueFilter, setDueFilter] = useState("");
  const [sort, setSort] = useState("overdue_first");
  const [selectedId, setSelectedId] = useState(null);
  const rowRefs = useRef([]);
  const originRow = useRef(0);
  // Phase C: "Log a call" — staff create request on behalf of a driver
  const [showLogCall, setShowLogCall] = useState(false);
  const [logPickReg, setLogPickReg] = useState("");
  const [logVehicle, setLogVehicle] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [notePickReg, setNotePickReg] = useState("");
  const [noteVehicle, setNoteVehicle] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteSource, setNoteSource] = useState("internal");
  const [notePerson, setNotePerson] = useState("");
  const [callsToday, setCallsToday] = useState(false);
  const allVehicles = useAsync(() => loadAccessibleVehicles(), []);

  // Desktop (xl) shows the existing booking detail in a reusable pane; smaller
  // screens keep the existing full-page detail route/behaviour.
  const openBooking = (bid) => {
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 1280px)").matches) {
      setSelectedId(bid);
    } else {
      nav(`/staff/booking/${bid}`);
    }
  };

  // Keyboard navigation over the currently visible (filtered + sorted) rows.
  // Never fires while typing in a form field; movement never triggers actions.
  const onRowKey = (e, i, id) => {
    const t = e.target;
    if (t && (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable)) return;
    if (e.key === "ArrowDown") { e.preventDefault(); rowRefs.current[Math.min(i + 1, rows.length - 1)]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); rowRefs.current[Math.max(i - 1, 0)]?.focus(); }
    else if (e.key === "Enter") { e.preventDefault(); originRow.current = i; openBooking(id); }
    else if (e.key === "Escape" && selectedId) { e.preventDefault(); setSelectedId(null); rowRefs.current[originRow.current]?.focus(); }
  };

  const savedViews = useAsync(async () => {
    const { data, error } = await supabase.from("saved_views").select("*").order("created_at");
    if (error) return [];
    return data || [];
  }, []);

  const applyView = (cfg) => {
    setQueue(cfg.queue ?? "all"); setQ(cfg.q ?? ""); setCustomer(cfg.customer ?? "");
    setStatus(cfg.status ?? ""); setMine(!!cfg.mine); setDueFilter(cfg.dueFilter ?? "");
    setSort(cfg.sort ?? "overdue_first");
  };

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. My overdue bookings):");
    if (!name) return;
    const config = { queue, q, customer, status, mine, dueFilter, sort };
    const { error } = await supabase.from("saved_views").insert({ user_id: uid, name, config });
    if (error) return toast.error(error.message);
    toast.success("View saved."); savedViews.reload();
  };

  const deleteView = async (id) => {
    const { error } = await supabase.from("saved_views").delete().eq("id", id);
    if (error) return toast.error(error.message);
    savedViews.reload();
  };

  // ---- shared views (Sprint 4) ----
  const role = profile?.role;
  const canPublish = ["administrator", "pod_lead", "account_manager"].includes(role);
  const sharedViews = useAsync(() => api.listSharedViews().catch(() => ({ shared_with_me: [], published_by_me: [] })), []);
  const [shareFor, setShareFor] = useState(null); // saved_view being published
  const [shareScope, setShareScope] = useState("role");
  const [shareTarget, setShareTarget] = useState("");
  const [shareBusy, setShareBusy] = useState(false);

  const openShare = (v) => {
    setShareFor(v);
    setShareScope(role === "pod_lead" ? "pod" : role === "account_manager" ? "customer" : "role");
    setShareTarget("");
  };

  const doPublish = async () => {
    if (shareScope !== "role" && !shareTarget) return toast.error("Pick a target");
    setShareBusy(true);
    try {
      await api.publishView({
        saved_view_id: shareFor.id,
        scope: shareScope,
        pod_id: shareScope === "pod" ? shareTarget : undefined,
        customer_id: shareScope === "customer" ? shareTarget : undefined,
        minimum_role: shareScope === "role" ? (shareTarget || "account_executive") : undefined,
      });
      toast.success("View published to your team.");
      setShareFor(null);
      sharedViews.reload();
    } catch (e) { toast.error(e.message || "Could not publish"); }
    finally { setShareBusy(false); }
  };

  const unshare = async (id) => {
    try { await api.unshareView(id); toast.success("Unpublished."); sharedViews.reload(); }
    catch (e) { toast.error(e.message); }
  };

  const copyShared = async (id) => {
    try { await api.copySharedView(id); toast.success("Copied to your saved views."); savedViews.reload(); }
    catch (e) { toast.error(e.message); }
  };

  const homePod = profile?.home_pod_id;
  const isAdmin = profile?.role === "administrator";
  const myCover = useMemo(() => new Set(data?.cover?.booking_request_ids || []), [data]);
  // Default dashboards are HOME-POD scoped. Cross-pod bookings I actively cover
  // still appear (they surface in My Work). Admins see all pods.
  const bookings = useMemo(() => {
    const all = data?.bookings || [];
    if (isAdmin || !homePod) return all;
    // Scope to the AE's OWN pod using the booking's ACTUAL owning pod (b.pod_id),
    // NOT the customer's pod — a booking routed to another pod (OOH / FM / cover)
    // must not leak into the normal queue and then fail the write RLS. Cover work
    // still surfaces via My Work.
    return all.filter((b) => b.pod_id === homePod || myCover.has(b.id));
  }, [data, isAdmin, homePod, myCover]);

  const counts = useMemo(() => {
    const c = {};
    QUEUES.forEach((qu) => { c[qu.key] = bookings.filter(qu.test).length; });
    return c;
  }, [bookings]);

  // "My calls today" — requests THIS AE logged on behalf of a driver today
  // (requester_kind='staff' + created_by me + created today). Counted across the
  // full dataset so cross-pod calls I logged are included, not just my home pod.
  const myCallsPredicate = (b) => b.requester_kind === "staff" && b.created_by_user_id === uid && isToday(b.created_at);
  const myCallsToday = useMemo(() => (data?.bookings || []).filter(myCallsPredicate).length, [data, uid]);

  // Calls Leaderboard — pod-level tally of requests logged on behalf this week.
  // Visible ONLY to Account Managers and Support Services members (per policy).
  const homePodName = (data?.ref?.pods || []).find((p) => p.id === homePod)?.name;
  const showLeaderboard = profile?.role === "account_manager" || homePodName === "Support Services";
  const leaderboard = useMemo(() => {
    if (!showLeaderboard) return [];
    const d = new Date(); const off = (d.getDay() + 6) % 7; d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - off);
    const staffMap = Object.fromEntries((data?.ref?.staff || []).map((s) => [s.id, s.full_name]));
    const podMap = Object.fromEntries((data?.ref?.pods || []).map((p) => [p.id, p.name]));
    const pods = {};
    (data?.bookings || []).forEach((b) => {
      if (b.requester_kind !== "staff" || !b.created_at || new Date(b.created_at) < d) return;
      const podName = podMap[b.pod_id] || "Unassigned pod";
      const ae = staffMap[b.created_by_user_id] || "Unknown";
      pods[podName] = pods[podName] || { total: 0, aes: {} };
      pods[podName].total++;
      pods[podName].aes[ae] = (pods[podName].aes[ae] || 0) + 1;
    });
    return Object.entries(pods).map(([pod, v]) => ({ pod, total: v.total,
      aes: Object.entries(v.aes).sort((a, b) => b[1] - a[1]) })).sort((a, b) => b.total - a.total);
  }, [data, showLeaderboard, homePod]);

  // Home-pod workload glance (Pod Leads & Account Managers)
  const showGlance = ["pod_lead", "account_manager"].includes(profile?.role) && homePod;
  const glance = useMemo(() => {
    const open = bookings.filter((b) => b.internal_status !== "completed" && b.internal_status !== "cancelled");
    return {
      open: open.length,
      overdue: open.filter((b) => updateBucket(b.next_update_due_at).key === "overdue").length,
      today: open.filter((b) => matchesDeadlineFilter(b, "today")).length,
      soon: open.filter((b) => updateBucket(b.next_update_due_at).key === "due_soon").length,
      unassigned: open.filter((b) => !b.assigned_staff_user_id).length,
    };
  }, [bookings]);

  const rows = useMemo(() => {
    // "My calls today" view: only the requests I logged on behalf today, across
    // all pods, newest first (ignores queue/customer/status filters).
    if (callsToday) {
      return (data?.bookings || []).filter(myCallsPredicate)
        .sort((a, b) => _cmpNull(_ts(a.created_at), _ts(b.created_at), false));
    }
    const qf = QUEUES.find((x) => x.key === queue);
    const filtered = bookings.filter((b) => {
      if (qf && !qf.test(b)) return false;
      if (!matchesDeadlineFilter(b, dueFilter)) return false;
      if (mine && b.assigned_staff_user_id !== uid && !myCover.has(b.id)) return false;
      if (customer && b.customer_id !== customer) return false;
      if (status && b.internal_status !== status) return false;
      if (q) {
        const hay = `${b.human_reference} ${b.vehicles?.registration || ""} ${b.drivers?.full_name || ""}`.toLowerCase();
        const nq = normalizeReg(q);
        const regMatch = nq && normalizeReg(b.vehicles?.registration).includes(nq);
        if (!hay.includes(q.toLowerCase()) && !regMatch) return false;
      }
      return true;
    });
    return filtered.sort((a, b) => {
      const esc = (isEscalated(b) ? 1 : 0) - (isEscalated(a) ? 1 : 0);
      if (esc !== 0) return esc; // escalated requests jump the queue
      return (SORTERS[sort] || SORTERS.overdue_first)(a, b);
    });
  }, [bookings, queue, dueFilter, mine, customer, status, q, sort, uid, myCover, callsToday, data]);

  if (loading) return <StaffShell><Spinner /></StaffShell>;

  return (
    <StaffShell>
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-clay))]">Operational queues</p>
          <h1 className="text-2xl md:text-[30px] font-semibold tracking-tight text-foreground">Good day, {profile?.full_name?.split(" ")[0]}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="my-calls-today" onClick={() => setCallsToday((v) => !v)} aria-pressed={callsToday}
            className={`inline-flex items-center gap-2 h-9 pl-3 pr-2.5 text-sm rounded-full border transition-colors ${callsToday ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary text-foreground"}`}>
            My calls today
            <span className={`font-mono text-xs font-semibold tabular-nums rounded-full px-1.5 py-0.5 ${callsToday ? "bg-primary-foreground/20" : "bg-secondary"}`}>{myCallsToday}</span>
          </button>
          <Btn variant="outline" data-testid="add-note-btn" onClick={() => setShowNote(true)}><Plus className="h-4 w-4" /> Add vehicle note</Btn>
          <Btn data-testid="log-call-btn" onClick={() => setShowLogCall(true)}><Plus className="h-4 w-4" /> Log a request</Btn>
          {showNote && (
            <ModalShell onClose={() => { setShowNote(false); setNoteVehicle(null); setNoteText(""); }} className="p-5 max-w-md" testId="note-shell">
              <div className="flex flex-col max-h-[80vh] text-left" data-testid="add-note-modal">
                {!noteVehicle ? (
                  <>
                    <h3 className="font-semibold mb-1">Add vehicle note — select the vehicle</h3>
                    <Input autoFocus data-testid="note-search" value={notePickReg} onChange={(e) => setNotePickReg(e.target.value)} placeholder="Search registration or customer" />
                    <div className="mt-3 flex-1 min-h-0 overflow-auto divide-y divide-border border border-border rounded-[8px]">
                      {(allVehicles.data || []).filter((v) => { const s = notePickReg.trim().toLowerCase(); return !s || `${v.registration} ${v.customers?.name}`.toLowerCase().includes(s); }).slice(0, 40).map((v) => (
                        <button key={v.id} type="button" data-testid={`note-pick-${formatReg(v.registration)}`} onClick={() => setNoteVehicle(v)} className="w-full text-left px-3 py-2.5 hover:bg-secondary/40 flex items-center justify-between gap-2">
                          <span className="vda-reg font-bold text-primary">{formatReg(v.registration)}</span>
                          <span className="text-xs text-muted-foreground truncate">{v.customers?.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold mb-1">Add note — {formatReg(noteVehicle.registration)}</h3>
                    <p className="text-xs text-muted-foreground mb-2">Entered by you; record who supplied the information below.</p>
                    <textarea data-testid="note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4} placeholder="What should be retained against this vehicle?" className="w-full text-sm rounded-[6px] border border-[hsl(var(--input))] p-2 bg-card" />
                    <label className="text-xs text-muted-foreground mt-2">Source of information</label>
                    <select data-testid="note-source" value={noteSource} onChange={(e) => setNoteSource(e.target.value)} className="h-9 rounded-[6px] border border-[hsl(var(--input))] px-2 bg-card text-sm">
                      {["internal", "fleet_manager", "driver", "supplier", "telephone", "email", "other"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                    <Input data-testid="note-person" value={notePerson} onChange={(e) => setNotePerson(e.target.value)} placeholder="Source person / entity (optional)" className="mt-2" />
                    <div className="flex justify-end gap-2 mt-3">
                      <Btn variant="outline" onClick={() => setNoteVehicle(null)}>Back</Btn>
                      <Btn data-testid="note-save" disabled={!noteText.trim()} onClick={async () => { try { await api.addVehicleNote({ vehicle_id: noteVehicle.id, note: noteText, source_category: noteSource, source_person: notePerson || null }); toast.success("Vehicle note saved to history."); setShowNote(false); setNoteVehicle(null); setNoteText(""); setNotePerson(""); } catch (e) { toast.error(e.message || "Could not save note."); } }}>Save note</Btn>
                    </div>
                  </>
                )}
              </div>
            </ModalShell>
          )}
          <Btn data-testid="my-calls-today-placeholder" className="hidden" />
        </div>
      </div>

      {/* Calls Leaderboard — AM & Support Services only (call-centre load, this week) */}
      {showLeaderboard && (
        <Panel className="p-4 mb-4" data-testid="calls-leaderboard">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="vda-tick text-[hsl(var(--vda-quiet))]">Calls logged on behalf · this week</span>
            <span className="text-xs text-muted-foreground">by pod &amp; AE</span>
          </div>
          {leaderboard.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="leaderboard-empty">No calls logged on behalf yet this week.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {leaderboard.map((row) => (
                <div key={row.pod} className="border border-border rounded-[8px] p-3" data-testid={`leaderboard-pod-${row.pod.replace(/\s+/g, "-")}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold truncate">{row.pod}</span>
                    <span className="font-mono text-[20px] leading-none font-semibold tabular-nums text-primary">{row.total}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {row.aes.map(([ae, n]) => (
                      <li key={ae} className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="truncate">{ae}</span>
                        <span className="font-mono tabular-nums">{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {/* Home-pod workload glance — Pod Leads & Account Managers */}
      {showGlance && (
        <Panel className="p-4 mb-4" data-testid="pod-glance">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="vda-tick text-[hsl(var(--vda-quiet))]">Home pod glance · {(data.ref.pods || []).find((p) => p.id === homePod)?.name || "Your pod"}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { k: "overdue", label: "Overdue", v: glance.overdue, tone: "red", onClick: () => { setQueue("overdue"); setDueFilter(""); } },
              { k: "today", label: "Due today", v: glance.today, tone: "amber", onClick: () => { setQueue("all"); setDueFilter("today"); } },
              { k: "soon", label: "Due soon", v: glance.soon, tone: "amber", onClick: () => { setQueue("soon"); setDueFilter(""); } },
              { k: "unassigned", label: "Unassigned", v: glance.unassigned, tone: "clay", onClick: () => { setQueue("new"); setDueFilter(""); } },
              { k: "open", label: "Open total", v: glance.open, tone: "green", onClick: () => { setQueue("all"); setDueFilter(""); } },
            ].map((m) => (
              <button key={m.k} data-testid={`glance-${m.k}`} onClick={m.onClick}
                className="text-left border border-border rounded-[8px] px-3 py-2.5 hover:bg-secondary transition-colors">
                <span className="vda-tick text-[hsl(var(--vda-quiet))] block">{m.label}</span>
                <span className={`font-mono text-[22px] leading-none font-semibold tabular-nums ${m.tone === "red" ? "text-[hsl(var(--vda-red))]" : m.tone === "amber" ? "text-[hsl(var(--vda-amber))]" : m.tone === "clay" ? "text-[hsl(var(--vda-clay))]" : "text-primary"}`}>{m.v}</span>
              </button>
            ))}
          </div>
        </Panel>
      )}

      {/* Operational summary strip — bordered cells, mono totals, urgent colours */}
      <div className="mt-4 overflow-x-auto border-y border-[hsl(var(--vda-strong-border))] bg-card" data-testid="queue-summary">
        <div className="flex min-w-max">
          {QUEUES.map((qu) => {
            const active = queue === qu.key;
            return (
              <button key={qu.key} data-testid={`queue-${qu.key}`} onClick={() => setQueue(qu.key)}
                className={`flex flex-col justify-center gap-1.5 min-h-[74px] px-4 text-left border-r border-border last:border-r-0 transition-colors ${active ? "bg-[hsl(var(--vda-green-bg))]" : "hover:bg-secondary"}`}>
                <span className={`vda-tick whitespace-nowrap ${active ? "text-primary" : "text-[hsl(var(--vda-quiet))]"}`}>{qu.label}</span>
                <span className={`font-mono text-[22px] leading-none font-semibold tabular-nums ${qu.tone === "red" ? "text-[hsl(var(--vda-red))]" : qu.tone === "amber" ? "text-[hsl(var(--vda-amber))]" : active ? "text-primary" : "text-foreground"}`}>{counts[qu.key]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* filters */}
      <Panel className="mt-5 p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, registration or driver" className="pl-9" />
        </div>
        <Select data-testid="filter-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} className="w-auto min-w-[160px]">
          <option value="">All customers</option>
          {data.ref.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[160px]">
          <option value="">All statuses</option>
          {INTERNAL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </Select>
        <label className="flex items-center gap-2 text-sm px-2">
          <input type="checkbox" data-testid="filter-mine" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Assigned to me
        </label>
      </Panel>

      {/* deadline quick-filters (compose with customer / status / assigned filters above) */}
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="deadline-filters">
        <span className="vda-tick text-muted-foreground mr-1">Deadline:</span>
        {DEADLINE_FILTERS.map((f) => (
          <button key={f.key || "all"} data-testid={`deadline-filter-${f.key || "all"}`}
            onClick={() => setDueFilter(f.key)}
            aria-pressed={dueFilter === f.key}
            className={`px-3 h-8 inline-flex items-center text-xs font-medium rounded-full border transition-colors ${dueFilter === f.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary text-foreground"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* sort + saved views */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="vda-tick text-muted-foreground mr-1">Sort:</span>
        <div className="w-56">
          <Select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select">
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </Select>
        </div>
        <Btn variant="outline" onClick={saveView} data-testid="save-view-btn"><Save className="h-4 w-4" /> Save view</Btn>
        {(savedViews.data || []).map((v) => (
          <span key={v.id} className="inline-flex items-center gap-1 pl-3 pr-1 h-8 text-xs rounded-full border border-border bg-secondary/40" data-testid={`saved-view-${v.id}`}>
            <button onClick={() => applyView(v.config || {})} className="font-medium" data-testid={`apply-view-${v.id}`}>{v.name}</button>
            {canPublish && <button onClick={() => openShare(v)} className="p-1 hover:text-[hsl(var(--vda-clay))]" title="Share with team" data-testid={`share-view-${v.id}`}><Share2 className="h-3 w-3" /></button>}
            <button onClick={() => deleteView(v.id)} className="p-1 hover:text-[hsl(var(--vda-red))]" data-testid={`delete-view-${v.id}`}><Trash2 className="h-3 w-3" /></button>
          </span>
        ))}
      </div>

      {/* shared / published views */}
      {((sharedViews.data?.shared_with_me || []).length > 0 || (sharedViews.data?.published_by_me || []).length > 0) && (
        <div className="mt-3 flex flex-col gap-2" data-testid="shared-views-section">
          {(sharedViews.data?.shared_with_me || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="vda-tick text-muted-foreground mr-1">Shared with me:</span>
              {sharedViews.data.shared_with_me.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 pl-3 pr-1 h-8 text-xs rounded-full border border-[hsl(var(--vda-clay))]/40 bg-[hsl(var(--vda-clay))]/5" data-testid={`shared-view-${s.id}`}>
                  <button onClick={() => applyView(s.saved_views?.config || {})} className="font-medium" data-testid={`apply-shared-${s.id}`}>{s.saved_views?.name || "Shared view"}</button>
                  <button onClick={() => copyShared(s.id)} className="p-1 hover:text-primary" title="Copy to my views" data-testid={`copy-shared-${s.id}`}><Copy className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          {(sharedViews.data?.published_by_me || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="vda-tick text-muted-foreground mr-1">Published by me:</span>
              {sharedViews.data.published_by_me.map((s) => {
                const pod = s.scope === "pod" ? (data.ref.pods || []).find((x) => x.id === s.pod_id) : null;
                const cust = s.scope === "customer" ? (data.ref.customers || []).find((x) => x.id === s.customer_id) : null;
                const audience = s.scope === "pod" ? (pod ? `Pod: ${pod.name}` : "Pod")
                  : s.scope === "customer" ? (cust ? cust.name : "Customer")
                  : s.scope === "role" ? (s.minimum_role ? `${ROLE_LABEL[s.minimum_role] || s.minimum_role} & above` : "By role")
                  : s.scope;
                return (
                  <span key={s.id} className="inline-flex items-center gap-1 pl-3 pr-1 h-8 text-xs rounded-full border border-border bg-secondary/40" data-testid={`published-view-${s.id}`}>
                    <span className="font-medium">{s.saved_views?.name || "View"}</span>
                    <Tag tone="muted">{audience}</Tag>
                    <button onClick={() => unshare(s.id)} className="p-1 hover:text-[hsl(var(--vda-red))]" title="Unpublish" data-testid={`unshare-${s.id}`}><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* publish modal */}
      {shareFor && (
        <ModalShell onClose={() => setShareFor(null)} className="p-5 max-w-md" testId="share-modal">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Share "{shareFor.name}"</h2>
              <button onClick={() => setShareFor(null)} className="p-1 text-muted-foreground" data-testid="share-close"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Publishing shares only the filter configuration. Each teammate still sees bookings under their own permissions.</p>
            <div className="space-y-3">
              <Field label="Scope">
                <Select value={shareScope} onChange={(e) => { setShareScope(e.target.value); setShareTarget(""); }} data-testid="share-scope-select">
                  {role === "pod_lead" && <option value="pod">My pod</option>}
                  {(role === "account_manager" || role === "administrator") && <option value="customer">A customer</option>}
                  {role === "administrator" && <option value="pod">A pod</option>}
                  <option value="role">By role (minimum)</option>
                </Select>
              </Field>
              {shareScope === "pod" && (
                <Field label="Pod">
                  <Select value={shareTarget} onChange={(e) => setShareTarget(e.target.value)} data-testid="share-target-select">
                    <option value="">Select pod…</option>
                    {(data.ref.pods || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </Field>
              )}
              {shareScope === "customer" && (
                <Field label="Customer">
                  <Select value={shareTarget} onChange={(e) => setShareTarget(e.target.value)} data-testid="share-target-select">
                    <option value="">Select customer…</option>
                    {(data.ref.customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </Field>
              )}
              {shareScope === "role" && (
                <Field label="Minimum role">
                  <Select value={shareTarget} onChange={(e) => setShareTarget(e.target.value)} data-testid="share-target-select">
                    <option value="account_executive">Account executive &amp; above</option>
                    <option value="account_manager">Account manager &amp; above</option>
                    <option value="pod_lead">Pod lead &amp; above</option>
                    <option value="administrator">Administrators only</option>
                  </Select>
                </Field>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setShareFor(null)} data-testid="share-cancel">Cancel</Btn>
              <Btn variant="clay" onClick={doPublish} loading={shareBusy} data-testid="share-publish-btn"><Share2 className="h-4 w-4" /> Publish</Btn>
            </div>
          </div>
        </ModalShell>
      )}

      {/* table (desktop) / cards (mobile) + selected-booking pane (xl) */}
      <div className={`mt-4 ${selectedId ? "xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:gap-5 xl:items-start" : ""}`}>
        <div className="min-w-0">
        {rows.length === 0 ? (
          <EmptyState title="No bookings in this view" hint="Adjust the filters or pick another queue." action={<Btn variant="outline" data-testid="empty-show-all" onClick={() => setQueue("all")}>Show all open work</Btn>} />
        ) : (
          <>
            <div className="hidden md:block vda-panel overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary text-left vda-tick text-[hsl(var(--vda-quiet))] border-b border-[hsl(var(--vda-strong-border))]">
                    <th className="px-4 py-2.5 font-medium">Registration</th>
                    <th className="px-4 py-2.5 font-medium">Customer</th>
                    <th className="px-4 py-2.5 font-medium">Type</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">Appointment</th>
                    <th className="px-4 py-2.5 font-medium">Update due</th>
                    <th className="px-4 py-2.5 font-medium">AE</th>
                    <th className="px-4 py-2.5 font-medium">Reference</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b, i) => {
                    const info = deadlineInfo(b.next_update_due_at);
                    return (
                      <tr key={b.id} data-testid={`booking-row-${b.human_reference}`} tabIndex={0}
                        ref={(el) => (rowRefs.current[i] = el)}
                        onClick={() => { originRow.current = i; openBooking(b.id); }}
                        onKeyDown={(e) => onRowKey(e, i, b.id)}
                        aria-label={`Booking ${b.human_reference}, ${formatReg(b.vehicles?.registration)}, ${b.public_status}`}
                        className={`border-t border-border cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${selectedId === b.id ? "bg-[hsl(var(--vda-green-bg))]" : "hover:bg-secondary/50 focus-visible:bg-[hsl(var(--vda-green-bg))]"}`}>
                        <td className="px-4 py-2.5"><span className="vda-reg font-bold tracking-wide text-foreground">{formatReg(b.vehicles?.registration)}</span></td>
                        <td className="px-4 py-2.5">{b.customers?.name}</td>
                        <td className="px-4 py-2.5">{REQUEST_LABEL[b.request_type] || b.request_type}</td>
                        <td className="px-4 py-2.5"><Tag tone="muted">{b.public_status}</Tag></td>
                        <td className="px-4 py-2.5 font-mono text-xs">{b.appointment_at ? fmtDate(b.appointment_at) : "—"}</td>
                        <td className="px-4 py-2.5">{b.internal_status === "completed" ? <span className="text-muted-foreground">—</span> : <div className="flex items-center gap-1.5"><DeadlineBadge info={info} testId={`deadline-${b.human_reference}`} />{isEscalated(b) ? <span data-testid={`escalated-badge-${b.human_reference}`}><Tag tone="red" className="whitespace-nowrap">Escalated ×{b.chase_count}</Tag></span> : (b.chase_count || 0) > 0 ? <span data-testid={`chase-badge-${b.human_reference}`}><Tag tone="amber" className="whitespace-nowrap">Chased ×{b.chase_count}</Tag></span> : null}</div>}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{b.assignee?.full_name || <span className="text-[hsl(var(--vda-clay))]">Unassigned</span>}</td>
                        <td className="px-4 py-2.5"><span className="font-mono text-[11px] text-[hsl(var(--vda-quiet))]">{b.human_reference}</span></td>
                        <td className="px-4 py-2.5"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-2">
              {rows.map((b) => {
                const info = deadlineInfo(b.next_update_due_at);
                return (
                  <button key={b.id} onClick={() => openBooking(b.id)} className="vda-panel p-4 w-full text-left">
                    <div className="flex justify-between items-start">
                      <span className="vda-reg text-base font-bold tracking-wide text-foreground">{formatReg(b.vehicles?.registration)}</span>
                      {b.internal_status !== "completed" && <DeadlineBadge info={info} testId={`deadline-m-${b.human_reference}`} />}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{b.customers?.name} · {REQUEST_LABEL[b.request_type]}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <Tag tone="muted">{b.public_status}</Tag>
                      <div className="flex items-center gap-1.5">
                        {isEscalated(b) ? <span data-testid={`escalated-badge-m-${b.human_reference}`}><Tag tone="red">Escalated ×{b.chase_count}</Tag></span> : (b.chase_count || 0) > 0 ? <span data-testid={`chase-badge-m-${b.human_reference}`}><Tag tone="amber">Chased ×{b.chase_count}</Tag></span> : null}
                        <span className="font-mono text-[11px] text-[hsl(var(--vda-quiet))]">{b.human_reference}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
        </div>
        {selectedId && (
          <aside className="hidden xl:block sticky top-4 max-h-[calc(100vh-2rem)] overflow-auto vda-panel p-4" data-testid="selected-booking-pane"
            onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setSelectedId(null); rowRefs.current[originRow.current]?.focus(); } }}>
            <StaffBookingDetail id={selectedId} embedded onClose={() => setSelectedId(null)} onChanged={reloadBookings} />
          </aside>
        )}
      </div>

      {/* Phase C: Log a call — fleet-wide vehicle picker → shared request form (creator auto-assigned) */}
      {showLogCall && (
        <ModalShell onClose={() => { setShowLogCall(false); setLogPickReg(""); }} className="p-5 max-w-md" testId="log-call-picker-shell">
          <div className="flex flex-col max-h-[80vh]" data-testid="log-call-picker">
            <h3 className="font-semibold mb-1">Log a request — select the vehicle</h3>
            <p className="text-xs text-muted-foreground mb-3">Search any fleet vehicle. The request routes to the customer's pod and is assigned to you.</p>
            <Input autoFocus data-testid="log-call-search" value={logPickReg} onChange={(e) => setLogPickReg(e.target.value)} placeholder="Search registration, customer or make/model" />
            <div className="mt-3 flex-1 min-h-0 overflow-auto divide-y divide-border border border-border rounded-[8px]">
              {(allVehicles.data || [])
                .filter((v) => { const s = logPickReg.trim().toLowerCase(); return !s || `${v.registration} ${v.make} ${v.model} ${v.customers?.name}`.toLowerCase().includes(s); })
                .slice(0, 40)
                .map((v) => (
                  <button key={v.id} type="button" data-testid={`log-call-pick-${formatReg(v.registration)}`}
                    onClick={() => { setLogVehicle(v); setShowLogCall(false); setLogPickReg(""); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-secondary/40 flex items-center justify-between gap-2">
                    <span className="vda-reg font-bold text-primary">{formatReg(v.registration)}</span>
                    <span className="text-xs text-muted-foreground truncate">{v.customers?.name} · {v.make} {v.model}</span>
                  </button>
                ))}
              {allVehicles.loading && <div className="p-4 text-sm text-muted-foreground">Loading fleet…</div>}
            </div>
            <div className="flex justify-end mt-3"><Btn variant="outline" onClick={() => { setShowLogCall(false); setLogPickReg(""); }}>Cancel</Btn></div>
          </div>
        </ModalShell>
      )}
      {logVehicle && (
        <NewRequestModal
          vehicle={logVehicle}
          hirePolicy={data.ref.customers.find((c) => c.id === logVehicle.customer_id)?.hire_policy || "on_request"}
          prefillDriver={{ name: "", mobile: "" }}
          context="staff"
          createFn={api.staffBooking}
          onClose={() => setLogVehicle(null)}
          onCreated={() => { setLogVehicle(null); toast.success("Request logged and assigned to you."); reloadBookings(); }}
        />
      )}
    </StaffShell>
  );
}
